import {
  exposedLocale, isLocalePreference, normalizeLanguageTag, resolveTelegramLocale, resolveWebLocale,
  type LocaleEnvironment, type LocalePreference, type PreviewLocale,
} from "@chatcode/i18n";
import { createWebI18n, loadWebLocale } from "@chatcode/i18n/web";

export interface LocaleSnapshot {
  preference: LocalePreference;
  locale: PreviewLocale;
}

export function createLocaleStore(options: {
  environment: LocaleEnvironment;
  languages: () => readonly string[];
  surface?: () => "web" | "telegram";
  storage: () => Pick<Storage, "getItem" | "setItem">;
  instance?: ReturnType<typeof createWebI18n>;
  preload?: (locale: PreviewLocale) => Promise<void>;
  onLanguage?: (locale: PreviewLocale) => void;
}) {
  const instance = options.instance ?? createWebI18n();
  const listeners = new Set<() => void>();
  const accountPreferences = new Map<string, LocalePreference>();
  let accountId: string | null = null;
  let telegramObservation: { userId: string | null; language: string; source: "launch" | "session" } | null = null;
  let generation = 0;
  let anonymousPreference: LocalePreference = "auto";
  try {
    const stored = options.storage().getItem("chatcode.locale");
    if (isLocalePreference(stored)) anonymousPreference = stored;
  } catch { /* Memory remains authoritative when storage is blocked. */ }
  let snapshot: LocaleSnapshot = { preference: anonymousPreference, locale: "en" };
  let requestedPreference = anonymousPreference;
  const storageKey = () => accountId ? `chatcode.locale.account:${accountId}` : "chatcode.locale";

  async function apply(preference: LocalePreference, persist: boolean, preview?: "en-XA"): Promise<boolean> {
    requestedPreference = preference;
    const revision = ++generation;
    const key = storageKey();
    const detected = options.surface?.() === "telegram"
      ? resolveTelegramLocale(preference, telegramObservation?.userId === accountId ? telegramObservation?.language : undefined)
      : resolveWebLocale(preference, options.languages());
    const locale = preview ?? exposedLocale(detected, options.environment);
    if (preview && options.environment === "production") throw new Error("pseudo-locale is not enabled");
    await (options.preload?.(locale) ?? loadWebLocale(instance, locale, options.environment));
    if (revision !== generation) return false;
    // Resources are already present. i18next commits synchronously, with no
    // backend load or Suspense boundary detaching a terminal/recording subtree.
    await instance.changeLanguage(locale);
    if (revision !== generation) return false;
    snapshot = { preference, locale };
    if (accountId && !preview) accountPreferences.set(accountId, preference);
    if (!accountId && !preview) anonymousPreference = preference;
    if (persist && !preview) {
      try { options.storage().setItem(key, preference); } catch { /* Still notify in-document consumers. */ }
    }
    options.onLanguage?.(locale);
    for (const listener of listeners) listener();
    return true;
  }

  return {
    instance,
    environment: options.environment,
    getAccountId: () => accountId,
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    initialize: () => apply(anonymousPreference, false),
    setPreference: (preference: LocalePreference) => apply(preference, true),
    async setAccount(userId: string | null, preference?: LocalePreference) {
      accountId = userId;
      if (!userId) return apply(anonymousPreference, false);
      if (preference !== undefined) return apply(preference, true);
      // Bootstrap is read-only until Settings resolves. Publishing a guessed
      // "auto" here would erase the shared choice in every other open tab.
      let cached = accountPreferences.get(userId);
      try {
        const stored = options.storage().getItem(storageKey());
        if (isLocalePreference(stored)) cached = stored;
      } catch { /* Use the same-account in-memory value if storage is blocked. */ }
      return apply(cached ?? "auto", false);
    },
    async setVerifiedTelegramLanguage(userId: string | null, language: unknown, source: "launch" | "session" = "launch") {
      const normalized = normalizeLanguageTag(language);
      if (!normalized) return;
      // Cached session metadata is a fallback, not permission to replay an
      // older observation over this Mini App's already-verified launch data.
      if (source === "session" && telegramObservation?.userId === userId && telegramObservation.source === "launch") return;
      telegramObservation = { userId, language: normalized, source };
      if (accountId === userId && options.surface?.() === "telegram") {
        await apply(requestedPreference, false);
      }
    },
    async refreshDetectedLanguage() {
      if (requestedPreference === "auto" && snapshot.locale !== "en-XA") await apply("auto", false);
    },
    previewPseudoLocale: () => apply(snapshot.preference, false, "en-XA"),
    async onStorage(event: Pick<StorageEvent, "key" | "newValue">) {
      if (event.key !== null && event.key !== storageKey()) return;
      // Clearing storage must not invent an account preference. The server is
      // authoritative; only explicit same-account cross-tab updates are applied.
      if (accountId && !isLocalePreference(event.newValue)) return;
      const preference = isLocalePreference(event.newValue) ? event.newValue : "auto";
      await apply(preference, false);
    },
  };
}

export type LocaleStore = ReturnType<typeof createLocaleStore>;
